import { useState } from "react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatTimeRange, formatHours } from "@/lib/format";
import { useIsMobile } from "@/hooks/use-mobile";
import type { LoggedEntryRef } from "@/hooks/use-budget-line-logged-hours";

/**
 * VIB-1201: viser underliggende føringer (dato + fra–til + varighet) for en
 * persons «Brukt»-tall i budsjett-tabben. Hover på desktop, tap/Popover på
 * touch. Ingen nye nettverkskall — entries kommer ferdig beregnet fra
 * computeBudgetLineLoggedHours (samme data som summen).
 *
 * String-matchede entries (usikker FK-kobling) merkes med "~" foran
 * klokkeslettet — flagges, skjules ikke (matcher endringssjekk-kravet).
 */
function EntriesList({
  userName,
  entries,
}: {
  userName: string;
  entries: LoggedEntryRef[];
}) {
  return (
    <div className="max-h-64 overflow-y-auto min-w-[220px]">
      <p className="text-xs font-semibold mb-1.5">{userName}</p>
      <ul className="space-y-1">
        {entries.map(({ entry, matchType }) => (
          <li
            key={entry.id}
            className="flex items-center justify-between gap-3 text-xs"
          >
            <span className="text-muted-foreground whitespace-nowrap">
              {entry.start_date
                ? format(parseISO(entry.start_date), "d. MMM", { locale: nb })
                : "—"}
            </span>
            <span className="tabular-nums whitespace-nowrap">
              {matchType === "string" && (
                <span
                  title="Usikker kobling (eldre string-matchet føring)"
                  className="text-muted-foreground mr-0.5"
                >
                  ~
                </span>
              )}
              {entry.start_date
                ? formatTimeRange(entry.start_date, entry.duration_seconds ?? 0)
                : "—"}
            </span>
            <span className="tabular-nums font-medium whitespace-nowrap">
              {formatHours((entry.duration_seconds ?? 0) / 3600)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LoggedEntriesTooltip({
  userName,
  entries,
  children,
}: {
  userName: string;
  entries: LoggedEntryRef[] | undefined;
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // Ingen entries å vise (f.eks. manual_hours_override uten koblede
  // føringer) — rendrer bare cellen uendret, ingen tom tooltip.
  if (!entries || entries.length === 0) return <>{children}</>;

  if (isMobile) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild onClick={() => setOpen((v) => !v)}>
          {children}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="end">
          <EntriesList userName={userName} entries={entries} />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="p-3" align="end">
        <EntriesList userName={userName} entries={entries} />
      </TooltipContent>
    </Tooltip>
  );
}
